import type { CurrencyCode } from 'codegen/types'

/**
 * Formats a currency amount from cents to a localized currency string.
 *
 * Assumes bigint values are within JavaScript's safe integer range
 * (<= 9007199254740991, i.e. ~9000 billion cents or ~90 billion dollars).
 * This only becomes a potential problem if we start supporting low-value currencies like IRR or IDR
 *
 * @param amount - Amount in cents (e.g., 1999 for $19.99)
 * @param currency - Currency code (USD, EUR, GBP)
 * @returns Formatted currency string (e.g., "$19.99", "€19.99", "£19.99")
 * @throws Error when bigint amount exceeds JavaScript's safe integer range
 */
export function formatCurrency(amount: number | bigint, currency: CurrencyCode): string {
  if (typeof amount === 'bigint' && amount > Number.MAX_SAFE_INTEGER) {
    throw new Error(
      `Currency amount ${amount} exceeds JavaScript's safe integer range and cannot be formatted`,
    )
  }
  // There is no bigint support in Intl.NumberFormat, so we have to convert to number
  const value = typeof amount === 'bigint' ? Number(amount) / 100 : amount / 100

  return new Intl.NumberFormat('en', {
    style: 'currency',
    currency,
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  }).format(value)
}

/**
 * Formats the per-1,000-coins unit price as a localized currency string, e.g. "$0.16".
 *
 * @param priceCents - Total pack price in cents (e.g., 399 for $3.99)
 * @param coins - Number of coins in the pack (e.g., 25_000)
 * @param currency - Currency code (USD, EUR, GBP)
 * @returns Formatted unit-price string (e.g., "$0.16", "€0.08")
 */
export function formatUnitPrice(priceCents: number, coins: number, currency: CurrencyCode): string {
  const perThousand = priceCents / 100 / (coins / 1000)
  return new Intl.NumberFormat('en', {
    style: 'currency',
    currency,
    minimumFractionDigits: 2,
    maximumFractionDigits: 3,
  }).format(perThousand)
}
